import { cva, type VariantProps } from 'class-variance-authority';
import { Plus } from 'lucide-react';
import * as React from 'react';

import { LongText } from './LongText';
import { Skeleton } from './Skeleton';
import { AutoneTooltip } from './Tooltip';
import { cn } from './utils';

const badgeVariants = cva(
  'w-fit border-none inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'bg-primary-10 text-primary-100',
        secondary: 'bg-secondary-10 text-secondary-100',
        error: 'bg-red-10 text-red-120',
        warning: 'bg-yellow-10 text-yellow-120',
        success: 'bg-green-10 text-green-120',
        grey: 'bg-grey-5 text-grey-80',
        loading: '',
        purple: 'bg-purple-10 text-purple-120',
        black: 'bg-black text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  if (variant === 'loading') {
    return (
      <Skeleton
        className={cn('h-[20px] w-[150px] rounded-md', className)}
        data-testid={props.id + '-skeleton'}
      />
    );
  }

  return (
    <div
      className={cn(badgeVariants({ variant }), className)}
      data-testid={props.id}
      {...props}
    />
  );
}

export { Badge, badgeVariants };

export interface BadgeGroupItem<Id extends string = string> {
  id: Id;
  label: string;
  variant?: VariantProps<typeof badgeVariants>['variant'];
}

interface BadgeGroupProps<Ids extends string = string> {
  badges: BadgeGroupItem<Ids>[];
  maxBadges?: number;
}

export const BadgeGroup = <Ids extends string>({
  badges,
  maxBadges = Infinity,
}: BadgeGroupProps<Ids>) => {
  const badgeCount = badges.length;
  const exceedsMaxBadges = badgeCount > maxBadges;

  if (!exceedsMaxBadges) {
    return badges.map((badge) => (
      <Badge key={badge.id} variant={badge.variant}>
        <LongText value={badge.label} maxLength={10} />
      </Badge>
    ));
  }

  const firstBadge = badges[0];

  return (
    <AutoneTooltip
      variant={'white'}
      text={
        <div className="flex flex-row gap-1 flex-wrap max-w-40">
          {badges.map((badge) => (
            <Badge key={badge.id} variant={badge.variant}>
              {badge.label}
            </Badge>
          ))}
        </div>
      }
    >
      <div className="flex flex-row gap-1">
        <Badge variant={firstBadge.variant}>
          <LongText value={firstBadge.label} maxLength={10} />
        </Badge>
        <Badge className="flex flex-row items-center whitespace-nowrap">
          <Plus size={10} />
          {badgeCount - 1}
        </Badge>
      </div>
    </AutoneTooltip>
  );
};
